/**
 * useUtmCapture - Captures UTM parameters and attribution data from URL.
 *
 * Runs once on mount: reads query params, stores in localStorage.
 * First-touch: only writes if no existing attribution.
 * Last-touch: always overwrites with fresh UTMs when present.
 * TTL: clears data older than 30 days.
 *
 * Usage: call useUtmCapture() in App.tsx (before any route).
 * On signup, call getAttribution() to include in the signup payload.
 */

import { useEffect } from 'react';

const STORAGE_KEY = 'cai_attribution';
const STORAGE_TS_KEY = 'cai_attribution_ts';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',           // short alias for referrer
  'influencer_id',
  'influencer',    // alias
  'coupon',
  'coupon_code',
  'ad_creative_id',
  'ad_id',
] as const;

function sanitize(value: string): string {
  return value.replace(/[<>"';{}\\]/g, '').trim().slice(0, 500);
}

export interface Attribution {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  referrer: string;
  landing_page: string;
  influencer_id: string;
  coupon_code: string;
  device_type: string;
  ad_creative_id: string;
  ad_id: string;
}

function detectDeviceType(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  return 'desktop';
}

/**
 * Extract UTM data from the current URL search params.
 * Returns null if no UTM params found.
 */
function extractUtmsFromUrl(): Partial<Attribution> | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const data: Record<string, string> = {};
    let hasAny = false;

    for (const key of UTM_PARAMS) {
      const value = params.get(key);
      if (value) {
        data[key] = sanitize(value);
        hasAny = true;
      }
    }

    if (!hasAny) return null;

    return {
      utm_source: data.utm_source || '',
      utm_medium: data.utm_medium || '',
      utm_campaign: data.utm_campaign || '',
      utm_content: data.utm_content || '',
      utm_term: data.utm_term || '',
      influencer_id: data.influencer_id || data.influencer || '',
      coupon_code: data.coupon_code || data.coupon || '',
      ad_creative_id: data.ad_creative_id || '',
      ad_id: data.ad_id || '',
    };
  } catch {
    return null;
  }
}

/**
 * Hook: captures UTMs from URL on mount and stores in localStorage.
 */
export function useUtmCapture(): void {
  useEffect(() => {
    try {
      // Check TTL — clear stale data
      const ts = localStorage.getItem(STORAGE_TS_KEY);
      if (ts && Date.now() - parseInt(ts, 10) > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_TS_KEY);
      }

      const utms = extractUtmsFromUrl();
      if (!utms) return; // No UTMs in URL, keep existing data

      const existing = localStorage.getItem(STORAGE_KEY);
      let merged: Partial<Attribution>;

      if (existing) {
        // Last-touch: overwrite with new UTMs but keep fields not present in new URL
        const prev = JSON.parse(existing) as Partial<Attribution>;
        merged = { ...prev, ...utms };
      } else {
        // First-touch: set everything fresh
        merged = { ...utms };
      }

      // Always set referrer, landing_page, device_type from current context
      merged.referrer = sanitize(document.referrer || '');
      merged.landing_page = sanitize(window.location.pathname + window.location.search);
      merged.device_type = detectDeviceType();

      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    } catch {
      // localStorage might be unavailable
    }
  }, []);
}

/**
 * Get stored attribution data for sending in signup payload.
 */
export function getAttribution(): Attribution {
  const empty: Attribution = {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    referrer: '',
    landing_page: '',
    influencer_id: '',
    coupon_code: '',
    device_type: detectDeviceType(),
    ad_creative_id: '',
    ad_id: '',
  };

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return empty;
    return { ...empty, ...JSON.parse(stored) };
  } catch {
    return empty;
  }
}

/**
 * Build an external checkout URL with the user's stored UTM params appended.
 */
export function buildCheckoutUrlWithUtms(baseUrl: string): string {
  const attribution = getAttribution();
  try {
    const url = new URL(baseUrl);
    if (attribution.utm_source) url.searchParams.set('utm_source', attribution.utm_source);
    if (attribution.utm_medium) url.searchParams.set('utm_medium', attribution.utm_medium);
    if (attribution.utm_campaign) url.searchParams.set('utm_campaign', attribution.utm_campaign);
    if (attribution.utm_content) url.searchParams.set('utm_content', attribution.utm_content);
    if (attribution.utm_term) url.searchParams.set('utm_term', attribution.utm_term);
    return url.toString();
  } catch {
    return baseUrl;
  }
}

/**
 * Clear stored attribution (call after successful signup if needed).
 */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TS_KEY);
  } catch {
    // ignore
  }
}
